import { splitUrl } from "@repo/fika-shared/src/lib/slug"
import { useParams } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { createMemo, Show } from "solid-js"
import { useApplication } from "@/context/session-provider"
import { Box } from "@/designSystem/components/box"
import { LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Fireworks } from "@/designSystem/patterns/fireworks"
import { Navigation } from "@/designSystem/patterns/navigation"
import { createFragment, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/get-title"
import { createBlogRouter } from "@/routes/posts-router"
import type { EditorPost } from "./post-editor-context"
import { PostEditorFragment } from "./post-editor-context/provider"
import {
  congratsContainerClass,
  contentWrapperClass,
  fireworksIconWrapperClass,
} from "./post-publish-share.css"
import { ShareContent } from "./post-share/share-content"

export function PostPublishShare() {
  const params = useParams()
  const id = createMemo(() => splitUrl(params.postId))
  const { LL } = useI18nContext()

  const PostPublishShareQuery = graphql(
    `query PostPublishShare($id: String!) {
      post(id: $id) {
        ...PostEditorFragment
      }
    }`,
    [PostEditorFragment],
  )
  const [postQueryResult] = createQuery({
    query: PostPublishShareQuery,
    variables: createMemo(() => ({ id: id() ?? "" })),
    pause: createMemo(() => !id()),
    requestPolicy: "cache-and-network",
  })

  const getPost = createFragment(
    PostEditorFragment,
    () => postQueryResult.data?.post,
  )

  return (
    <Show
      when={!postQueryResult.fetching && getPost()}
      fallback={<div>{LL().common.loading()}</div>}
    >
      {post => <SafePostPublishShare post={post()} />}
    </Show>
  )
}

function SafePostPublishShare(props: { post: EditorPost }) {
  const { user } = useApplication()
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const mainVersion = createMemo(() => {
    const mainLocale = props.post.mainLocale
    return (
      props.post.currentVersions?.find(v => v.locale === mainLocale) ?? null
    )
  })
  const title = createMemo(() => getTitle(mainVersion()?.title ?? ""))

  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
      {
        label: title(),
        href: paths().post({ id: props.post.id, title: title() }).url,
      } as const,
    ]
  })

  return (
    <MainLayout
      hue="emerald"
      header={
        <Navigation
          hue="emerald"
          buttons={buttons()}
          title={LL().post.share()}
        />
      }
    >
      <div class={contentWrapperClass}>
        <Box gap="24" padding={{ block: "16" }}>
          <div class={congratsContainerClass}>
            <div class={fireworksIconWrapperClass}>
              <Fireworks />
              <Icon name="party-popper" size="32" color="emerald700" />
            </div>
            <Text
              fontSize="l"
              fontFamily="accent"
              fontWeight="600"
              color="emerald900"
            >
              {LL().postShare.publishedCongrats()}
            </Text>
            <Text fontSize="m" color="sand700">
              {LL().postShare.shareNow()}
            </Text>
          </div>

          <ShareContent post={props.post} userName={user.publication!.name} />

          <Box
            flexDirection="row"
            justifyContent="center"
            padding={{ block: "8" }}
          >
            <LinkButton
              hue="emerald"
              href={paths().post({ id: props.post.id, title: title() }).url}
            >
              <Icon name="arrow-left" size="18" />
              {LL().postShare.backToPost()}
            </LinkButton>
          </Box>
        </Box>
      </div>
    </MainLayout>
  )
}
